import { useEffect, useState } from 'react';
import { Button, IconHighlight, ModalLayout, Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { getAccessRemovalImpact } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import ModalFooter from '../../components/modals/parts/ModalFooter.jsx';
import { AlertTriangle } from 'lucide-react';

const IMPACT_KEYS = ['workflows', 'agents', 'widgets'];

/**
 * Access-remove confirmation (prototype `openAccessRemoveModal`): title +
 * immediate-effect subtitle, amber impact counts, outlined destructive confirm.
 */
function AccessRemoveModal({ instanceId, entry, onClose, onConfirm }) {
  const { t } = useTranslation('admin-studio');
  const [impact, setImpact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!entry) return undefined;
    // isLoading initializes to true; the modal remounts per open (AccessTab
    // renders it conditionally), so no synchronous reset is needed here.
    let cancelled = false;
    getAccessRemovalImpact(instanceId, entry.id)
      .then((res) => {
        if (!cancelled) setImpact(validateApiResponse(res, t('common.requestFailed')).body);
      })
      .catch(() => {
        if (!cancelled) setImpact({ workflows: 0, agents: 0, widgets: 0 });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entry, instanceId, t]);

  if (!entry) return null;

  const rows = IMPACT_KEYS.map((key) => ({
    key,
    count: impact?.[key] ?? 0,
    label: t(`integrations.instance.access.impact.${key}.label`),
    desc: t(`integrations.instance.access.impact.${key}.desc`),
  })).filter((row) => row.count > 0);

  return (
    <ModalLayout
      isOpen
      onClose={onClose}
      useBodyPortal
      maxWidthClassName="max-w-[600px]"
      title={t('integrations.instance.access.removeTitle', { name: entry.name })}
      subtitle={t('integrations.instance.access.removeHint')}
      titlePrefix={
        <IconHighlight
          icon={AlertTriangle}
          size="medium"
          bgColor="Error"
          iconColor="Error"
          className="border border-red-500/40"
        />
      }
    >
      <div className="px-6 py-4">
        {isLoading ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <div className="overflow-hidden rounded-[10px] border border-edge">
            <p className="border-b border-edge bg-white/[0.02] px-3.5 py-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-content-faint">
              {t('integrations.instance.access.impactHeading')}
            </p>
            {rows.length === 0 ? (
              <p className="px-3.5 py-4 text-[12.5px] text-content-secondary">
                {t('integrations.instance.access.impactEmpty')}
              </p>
            ) : (
              <ul>
                {rows.map((row, index) => (
                  <li
                    key={row.key}
                    className={[
                      'flex items-center gap-3 px-3.5 py-[11px]',
                      index < rows.length - 1 ? 'border-b border-edge' : '',
                    ].join(' ')}
                  >
                    <span className="w-7 shrink-0 text-center font-mono text-[18px] font-bold tabular-nums text-[#FBBF24]">
                      {row.count}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-content">{row.label}</p>
                      <p className="text-[11.5px] text-content-faint">{row.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <ModalFooter className="!px-6 !py-3.5">
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onConfirm}
          className="!border !border-[rgba(239,68,68,0.2)] !bg-[rgba(239,68,68,0.10)] !text-[#EF4444] hover:!bg-[rgba(239,68,68,0.18)]"
        >
          {t('integrations.instance.access.removeAccess')}
        </Button>
      </ModalFooter>
    </ModalLayout>
  );
}

export default AccessRemoveModal;
